import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import DOMPurify from "dompurify"
import { Marked } from "marked"
import { withMediaTokens } from "../../api/client"
import { cx } from "../ui"
import { ImageLightbox } from "./ImageLightbox"

/**
 * Read-only rendering of a stored body, with click-to-zoom on screenshots.
 *
 * Two formats arrive: HTML from the editor, and Markdown written by an agent
 * through the API. Markdown is turned into HTML here rather than on the server,
 * which keeps the backend free of a parser. Both then go through DOMPurify -
 * HTML is sanitised on write as well, so for that format this is defence in
 * depth rather than the only control.
 */

const markdown = new Marked({ gfm: true, breaks: false, async: false })

// Links in a body always leave the app. Set after sanitising, so the attribute
// is ours rather than something the body asked for.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
	if (node.tagName === "A" && node.getAttribute("href")) {
		node.setAttribute("target", "_blank")
		node.setAttribute("rel", "noopener noreferrer")
	}
})

/** Body text as safe HTML, attachment images carrying the viewer's token. */
export function renderBody(body, format = "html") {
	if (!body) return ""
	const html = format === "markdown" ? markdown.parse(body) : body
	return withMediaTokens(DOMPurify.sanitize(html))
}

export function RichBody({ body, format, className }) {
	const hostRef = useRef(null)
	const [lightbox, setLightbox] = useState(null)
	const html = useMemo(() => renderBody(body, format), [body, format])
	const closeLightbox = useCallback(() => setLightbox(null), [])

	useEffect(() => {
		const host = hostRef.current
		if (!host) return undefined
		const onClick = (event) => {
			// An image inside a link follows the link instead.
			if (event.target.tagName === "IMG" && !event.target.closest("a")) {
				setLightbox({
					src: event.target.src,
					filename: event.target.getAttribute("alt") || ""
				})
			}
		}
		host.addEventListener("click", onClick)
		return () => host.removeEventListener("click", onClick)
	}, [html])

	if (!html) return null

	return (
		<>
			<div
				ref={hostRef}
				className={cx("prose-note", className)}
				dangerouslySetInnerHTML={{ __html: html }}
			/>
			<ImageLightbox
				src={lightbox?.src}
				filename={lightbox?.filename}
				onClose={closeLightbox}
			/>
		</>
	)
}
